import * as React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { graphql } from "gatsby";
import { PrismicRichText, PrismicLink } from "@prismicio/react";
import { GatsbyImage } from "gatsby-plugin-image";

const SocialIcon = ({ link, image, fallbackLabel }) => {
  if (!link || !image?.gatsbyImageData) return null;
  return (
    <PrismicLink field={link} className="social-link">
      <GatsbyImage
        className="social-icon"
        image={image.gatsbyImageData}
        alt={image.alt || fallbackLabel}
      />
    </PrismicLink>
  );
};

const FooterLinkColumn = ({ heading, links, columnKey }) => (
  <nav
    aria-label={heading?.text || `Footer links ${columnKey}`}
    className="footer-col"
  >
    <PrismicRichText
      field={heading?.richText}
      components={{
        heading5: ({ children }) => (
          <h2 className="footer-col-heading">{children}</h2>
        ),
      }}
    />
    <ul className="footer-link-list">
      {(links || []).map((item, i) => (
        <li key={`${columnKey}-${i}`}>
          <PrismicLink field={item.link} className="footer-link">
            <PrismicRichText
              field={item.label?.richText}
              components={{
                paragraph: ({ children }) => <span>{children}</span>,
              }}
            />
          </PrismicLink>
        </li>
      ))}
    </ul>
  </nav>
);

export const Footer = ({ footer }) => {
  if (!footer) return null;

  return (
    <footer className="footer" role="contentinfo">
      <div className="footer-inner">
        <Container>
          <Row className="footer-grid">
            <Col xs={12} md={6} xl={3}>
              <FooterLinkColumn
                heading={footer.firstcategoryname}
                links={footer.first_category_links}
                columnKey="first"
              />
            </Col>
            <Col xs={12} md={6} xl={3}>
              <FooterLinkColumn
                heading={footer.second_category_name}
                links={footer.second_category_links}
                columnKey="second"
              />
            </Col>
            <Col xs={12} md={6} xl={3}>
              <FooterLinkColumn
                heading={footer.third_category_name}
                links={footer.third_category_links}
                columnKey="third"
              />
              <div className="footer-social-row" aria-label="Social media">
                <SocialIcon
                  link={footer.social_link_1}
                  image={footer.social_image_1}
                  fallbackLabel="Social link 1"
                />
                <SocialIcon
                  link={footer.social_link_2}
                  image={footer.social_image_2}
                  fallbackLabel="Social link 2"
                />
                <SocialIcon
                  link={footer.social_link_3}
                  image={footer.social_image_3}
                  fallbackLabel="Social link 3"
                />
                <SocialIcon
                  link={footer.social_link_4}
                  image={footer.social_image_4}
                  fallbackLabel="Social link 4"
                />
                <SocialIcon
                  link={footer.social_link_5}
                  image={footer.social_image_5}
                  fallbackLabel="Social link 5"
                />
              </div>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <div className="footer-col">
                <PrismicRichText
                  field={footer.title?.richText}
                  components={{
                    heading5: ({ children }) => (
                      <h2 className="footer-col-heading">{children}</h2>
                    ),
                  }}
                />
                <PrismicRichText
                  field={footer.subtitle?.richText}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="footer-subtitle">{children}</p>
                    ),
                  }}
                />
                <div className="footer-app-badges">
                  {footer.first_link_image?.gatsbyImageData && (
                    <PrismicLink
                      field={footer.first_link}
                      className="footer-app-badge"
                    >
                      <GatsbyImage
                        image={footer.first_link_image.gatsbyImageData}
                        alt={footer.first_link_image.alt || "Download on the App Store"}
                      />
                    </PrismicLink>
                  )}
                  {footer.second_link_image?.gatsbyImageData && (
                    <PrismicLink
                      field={footer.second_link}
                      className="footer-app-badge"
                    >
                      <GatsbyImage
                        image={footer.second_link_image.gatsbyImageData}
                        alt={footer.second_link_image.alt || "Get it on Google Play"}
                      />
                    </PrismicLink>
                  )}
                </div>
              </div>
            </Col>
          </Row>

          <hr className="footer-divider" />

          <Row className="footer-legal">
            <Col xs={12} md="auto" className="footer-legal-copy">
              © {new Date().getFullYear()} Grub24 Limited
              <span className="footer-legal-sep">•</span>
              Company number 11873507
            </Col>
            <Col xs={12} md="auto" className="footer-legal-links">
              <a href="/privacy-policy" className="footer-legal-link">
                Privacy
              </a>
              <a href="/terms" className="footer-legal-link">
                Terms
              </a>
              <a href="/cookies" className="footer-legal-link">
                Cookies
              </a>
            </Col>
          </Row>
        </Container>
      </div>
      <script src="//eu.fw-cdn.com/12756267/746640.js" chat="true" async defer></script>
    </footer>
  );
};

export const query = graphql`
  fragment FooterFragment on PrismicFooter {
    _previewable
    type
    lang
    data {
      title {
        richText
      }
      subtitle {
        richText
      }
      first_link {
        url
        target
        link_type
      }
      first_link_image {
        gatsbyImageData
        alt
      }
      second_link {
        url
        target
        link_type
      }
      second_link_image {
        gatsbyImageData
        alt
      }
      firstcategoryname {
        richText
        text
      }
      first_category_links {
        link {
          url
          target
          link_type
        }
        label {
          richText
        }
      }
      second_category_name {
        richText
        text
      }
      second_category_links {
        link {
          url
          target
          link_type
        }
        label {
          richText
        }
      }
      third_category_name {
        richText
        text
      }
      third_category_links {
        link {
          url
          target
          link_type
        }
        label {
          richText
        }
      }
      social_link_1 {
        url
        target
        link_type
      }
      social_image_1 {
        gatsbyImageData
        alt
      }
      social_link_2 {
        url
        target
        link_type
      }
      social_image_2 {
        gatsbyImageData
        alt
      }
       social_link_3 {
        url
        target
        link_type
      }
      social_image_3 {
        gatsbyImageData
        alt
      }
      social_link_4 {
        url
        target
        link_type
      }
      social_image_4 {
        gatsbyImageData
        alt
      }
      social_link_5 {
        url
        target
        link_type
      }
      social_image_5 {
        gatsbyImageData
        alt
      }
    }
  }
`;
